import { router } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';

import { workOrderRowLabel } from './dashboard-summary-card';

import { Button, Modal, Text } from '@/components/ui';
import { useApprovalQueue } from '@/hooks/use-approval-queue';
import { useBulkApproveAll } from '@/hooks/use-work-order-review';
import { Alert } from '@/utils/alert';
import { approvalSlaLabel } from '@/utils/approval-sla';


/**
 * A blocking list of OVERDUE approvals, once per session (#496).
 *
 * ── WHY ONLY OVERDUE ──
 *
 * The target is approval inside two business days. Interrupting a property manager for work
 * that is still inside its window would train them to dismiss this on reflex, and then it
 * is not available for the case it exists for. Worse, a modal over routine work is a modal
 * a PM has to clear during a storm callout.
 *
 * ── WHY ONCE PER SESSION, AND NOT PERSISTED ──
 *
 * Session-scoped, not stored in a preference. "Remind me later" has to mean later THIS
 * session, because a durable dismissal is indistinguishable from the missed approvals this
 * whole feature replaces — someone clicks it once and never sees the prompt again. A new
 * sign-in re-prompts, which is the point: the banner carries the reminder in between.
 *
 * The flag is module-level rather than component state because the dashboard remounts on
 * navigation; component state would re-fire the modal every time a PM came back to the
 * home tab.
 */

let dismissedThisSession = false;

/** Reset hook for tests — the module-level flag would otherwise leak between them. */
export const __resetApprovalInterstitial = (): void => { dismissedThisSession = false; };

/** How many rows to list before "…and N more". This is a prompt, not the queue. */
const MAX_LISTED = 8;

export const ApprovalInterstitial: React.FC = () => {
	/**
	 * The queue hook supplies the rows, so this shows the same overdue set as the banner and
	 * the To Do panel. Accounting's queue has no SLA, so `overdueCount` is zero for them and
	 * the modal never fires — no role check needed.
	 */
	const { overdue, overdueCount, isLoading, refetch } = useApprovalQueue();
	const { approveAll, isPending: approving } = useBulkApproveAll();
	const [visible, setVisible] = useState(false);

	/**
	 * `dismissedThisSession` is intentionally NOT a dependency.
	 *
	 * It is a module-level flag, so mutating it does not (and must not) re-render — it is
	 * read here at effect time, which is exactly the semantics wanted: once dismissed, no
	 * later change in `overdueCount` re-opens the modal for the rest of the session.
	 */
	useEffect(() => {
		if (dismissedThisSession || isLoading) return;
		if (overdueCount > 0) setVisible(true);
	}, [isLoading, overdueCount]);

	const dismiss = useCallback(() => {
		dismissedThisSession = true;
		setVisible(false);
	}, []);

	const reviewNow = useCallback(() => {
		dismiss();
		// The first overdue work order, since "review" means looking at one.
		const first = overdue[0]?.workOrder;
		if (first) router.push({ pathname: '/work-orders/[id]', params: { id: first.id } });
	}, [dismiss, overdue]);

	const approveEverything = useCallback(async () => {
		const ids = overdue.map((r) => r.workOrder.id);
		if (ids.length === 0) return;
		try {
			const totals = await approveAll(ids);
			dismiss();
			await refetch();
			const parts = [`${totals.approved} approved`];
			if (totals.skipped > 0) parts.push(`${totals.skipped} already approved`);
			if (totals.errors.length > 0) parts.push(`${totals.errors.length} failed`);
			Alert.alert(totals.errors.length > 0 ? 'Approved with errors' : 'Approved', parts.join(' · '));
		} catch (e) {
			await refetch();
			Alert.alert('Could not approve', (e as Error).message);
		}
	}, [overdue, approveAll, dismiss, refetch]);

	if (!visible || overdueCount === 0) return null;

	const listed = overdue.slice(0, MAX_LISTED);
	const nowMs = Date.now();

	return (
		<Modal visible onDismiss={dismiss} testID="approval-interstitial">
			<Text style={styles.title}>
				{overdueCount} work order{overdueCount === 1 ? '' : 's'} overdue for your approval
			</Text>
			<Text style={styles.body}>
				Accounting has reviewed {overdueCount === 1 ? 'this' : 'these'} and is waiting on you to
				invoice the association. Approvals are due within two business days.
			</Text>

			<ScrollView style={styles.list}>
				{listed.map(({ workOrder: wo, sla }) => (
					<View key={wo.id} style={styles.row}>
						<View style={styles.rowMain}>
							<Text numberOfLines={1} style={styles.rowTitle}>{workOrderRowLabel(wo)}</Text>
							{wo.customerDescription ? (
								<Text numberOfLines={2} style={styles.rowNote}>{wo.customerDescription}</Text>
							) : null}
						</View>
						{sla ? <Text style={styles.rowSla}>{approvalSlaLabel(sla, nowMs)}</Text> : null}
					</View>
				))}
				{overdueCount > listed.length ? (
					<Text style={styles.more}>…and {overdueCount - listed.length} more.</Text>
				) : null}
			</ScrollView>

			<View style={styles.actions}>
				{/*
					"Remind me later" is deliberately the least prominent of the three, and
					deliberately present: a modal with no way out is one a PM will work around
					by not signing in.
				*/}
				<Button onPress={dismiss} disabled={approving} testID="btn-interstitial-later">
					<Text>Remind me later</Text>
				</Button>
				<Button mode="outlined" onPress={reviewNow} disabled={approving} testID="btn-interstitial-review">
					<Text>Review</Text>
				</Button>
				<Button
					mode="contained"
					onPress={approveEverything}
					disabled={approving}
					testID="btn-interstitial-approve-all"
				>
					<Text>{approving ? 'Approving…' : `Approve all ${overdueCount}`}</Text>
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	title: { fontSize: 20, fontWeight: '700', color: '#c62828', marginBottom: 8 },
	body: { fontSize: 14, color: '#455a64', lineHeight: 20, marginBottom: 12 },
	list: { maxHeight: Platform.OS === 'web' ? 320 : 260 },
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		paddingVertical: 8,
		borderBottomWidth: 1,
		borderBottomColor: '#eee',
	},
	rowMain: { flex: 1, minWidth: 0 },
	rowTitle: { fontSize: 14, fontWeight: '600' },
	rowNote: { fontSize: 13, color: '#607d8b', marginTop: 2 },
	rowSla: { fontSize: 12, fontWeight: '700', color: '#c62828' },
	more: { color: '#78909c', fontStyle: 'italic', paddingVertical: 8 },
	actions: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		gap: 8,
		marginTop: 16,
		flexWrap: 'wrap',
	},
});
